import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Button,
    CircularProgress,
    Divider,
    Chip,
} from "@mui/material";
import CountUp from "react-countup";
import ReactApexChart from "react-apexcharts";
import { getBpGstVerificationLisitng, getBcSalesStats } from "../../api";
import moment from "moment";

// ─── BC Sales Stats Widget ───────────────────────────────────────────────────

const bcStatCards = [
    { key: "total_records",          label: "Total Records",    color: "#1565c0", bg: "#e3f2fd" },
    { key: "total_documents",        label: "Unique Docs",      color: "#2e7d32", bg: "#e8f5e9" },
    { key: "total_bc_sales_numbers", label: "BC Sales Nos",     color: "#6a1b9a", bg: "#f3e5f5" },
    { key: "success_count",          label: "Success",          color: "#2e7d32", bg: "#e8f5e9" },
    { key: "failed_count",           label: "Failed",           color: "#c62828", bg: "#ffebee" },
    { key: "pending_count",          label: "Pending",          color: "#e65100", bg: "#fff3e0" },
];

const BcSalesStatsWidget = () => {
    const [startDate, setStartDate] = useState("2026-05-01");
    const [endDate, setEndDate] = useState("2026-09-30");
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const payload = {
                start_date: `${startDate} 00:00:00`,
                end_date: `${endDate} 23:59:59`,
            };
            const res = await getBcSalesStats(payload);
            setStats(res?.data?.result ?? null);
            setFetched(true);
        } catch (err) {
            console.error("getBcSalesStats error", err);
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch on mount with defaults
    useEffect(() => {
        fetchStats();
    }, []);

    return (
        <Card
            elevation={0}
            sx={{
                mb: 4,
                borderRadius: 4,
                border: "1.5px solid #e3eafc",
                background: "linear-gradient(135deg, #f5f7ff 0%, #fff 100%)",
                overflow: "visible",
            }}
        >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                {/* Widget Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 3,
                                background: "linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontSize: 22,
                                boxShadow: "0 4px 14px rgba(21,101,192,0.25)",
                            }}
                        >
                            📊
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={700} color="#1a237e" lineHeight={1.2}>
                                BC Sales Posting Stats
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Documents with bc_sales_number — grouped by posting status
                            </Typography>
                        </Box>
                    </Box>

                    {/* Date Range Filter */}
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                bgcolor: "#fff",
                                px: 2,
                                py: 1,
                                borderRadius: 3,
                                border: "1px solid #dde3f0",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" fontWeight={500} noWrap>
                                From
                            </Typography>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                style={{
                                    border: "none",
                                    outline: "none",
                                    fontSize: 14,
                                    color: "#1a237e",
                                    background: "transparent",
                                    fontFamily: "inherit",
                                    cursor: "pointer",
                                }}
                            />
                            <Typography variant="body2" color="text.secondary" fontWeight={500} noWrap>
                                To
                            </Typography>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                style={{
                                    border: "none",
                                    outline: "none",
                                    fontSize: 14,
                                    color: "#1a237e",
                                    background: "transparent",
                                    fontFamily: "inherit",
                                    cursor: "pointer",
                                }}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            size="small"
                            onClick={fetchStats}
                            disabled={loading}
                            sx={{
                                borderRadius: 2.5,
                                px: 3,
                                py: 1,
                                fontWeight: 600,
                                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                                boxShadow: "0 4px 12px rgba(21,101,192,0.3)",
                                textTransform: "none",
                                "&:hover": {
                                    background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
                                },
                            }}
                        >
                            {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Apply Filter"}
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ mb: 3, borderColor: "#e8edf8" }} />

                {/* Stat Cards */}
                {loading && !fetched ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {bcStatCards.map((card) => {
                            const value = stats ? (Number(stats[card.key]) || 0) : 0;
                            return (
                                <Grid item xs={6} sm={4} md={2} key={card.key}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            borderRadius: 3.5,
                                            border: `1.5px solid ${card.color}22`,
                                            bgcolor: card.bg,
                                            transition: "transform 0.2s, box-shadow 0.2s",
                                            "&:hover": {
                                                transform: "translateY(-4px)",
                                                boxShadow: `0 8px 24px ${card.color}28`,
                                            },
                                            position: "relative",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {/* Decorative arc */}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: -18,
                                                right: -18,
                                                width: 60,
                                                height: 60,
                                                borderRadius: "50%",
                                                bgcolor: `${card.color}18`,
                                            }}
                                        />
                                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                            <Typography
                                                variant="caption"
                                                fontWeight={600}
                                                color={card.color}
                                                display="block"
                                                mb={0.5}
                                                title={card.label}
                                                sx={{
                                                    letterSpacing: 0.3,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {card.label}
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                fontWeight={800}
                                                color={card.color}
                                                lineHeight={1.1}
                                            >
                                                {stats ? (
                                                    <CountUp
                                                        end={value}
                                                        duration={1.6}
                                                        separator=","
                                                    />
                                                ) : (
                                                    "—"
                                                )}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {/* Date range label */}
                {fetched && stats && (
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <Chip
                            label={`${moment(startDate).format("DD MMM YYYY")} – ${moment(endDate).format("DD MMM YYYY")}`}
                            size="small"
                            sx={{
                                bgcolor: "#e3f2fd",
                                color: "#1565c0",
                                fontWeight: 600,
                                fontSize: 11,
                                borderRadius: 2,
                            }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

// ─── Main BpGstVerification Component ────────────────────────────────────────

const BpGstVerification = () => {
    const [data, setData] = useState([]);
    const [pagination] = useState({ pageIndex: 0, pageSize: 10 });
    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

    const months = [
        { value: 1, label: "January" }, { value: 2, label: "February" },
        { value: 3, label: "March" }, { value: 4, label: "April" },
        { value: 5, label: "May" }, { value: 6, label: "June" },
        { value: 7, label: "July" }, { value: 8, label: "August" },
        { value: 9, label: "September" }, { value: 10, label: "October" },
        { value: 11, label: "November" }, { value: 12, label: "December" },
    ];

    const years = Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - 5 + i);

    const FetchGstVerificationListing = async () => {
        try {
            const payload = {
                page: pagination.pageIndex,
                limit: pagination.pageSize,
            };
            const result = await getBpGstVerificationLisitng(payload);
            setData(result?.data?.result ?? []);
        } catch (error) {
            console.error("FetchGstVerificationListing error", error);
        }
    };

    useEffect(() => {
        FetchGstVerificationListing();
    }, []);

    const filteredData = data.filter((item) => {
        const date = new Date(item.verification_date);
        return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
    });

    const monthData = filteredData.sort(
        (a, b) => new Date(b.verification_date) - new Date(a.verification_date)
    )[0];

    const gstCards = [
        { title: "Total Active BP", value: monthData?.total_bp_count ?? 0,  color: "#1976d2" },
        { title: "Active",          value: monthData?.active_count ?? 0,     color: "#2e7d32" },
        { title: "Suspended",       value: monthData?.suspended_count ?? 0,  color: "#ed6c02" },
        { title: "Cancelled",       value: monthData?.cancelled_count ?? 0,  color: "#d32f2f" },
        { title: "Inactive",        value: monthData?.inactive_count ?? 0,   color: "#6a1b9a" },
    ];

    const pieChartOptions = {
        chart: { type: "pie", toolbar: { show: false } },
        labels: ["Active", "Suspended", "Cancelled", "Inactive"],
        legend: { position: "bottom" },
        colors: ["#2e7d32", "#ed6c02", "#d32f2f", "#6a1b9a"],
        dataLabels: { enabled: true },
        tooltip: { y: { formatter: (val) => `${val} BP` } },
    };

    const pieChartSeries = [
        monthData?.active_count ?? 0,
        monthData?.suspended_count ?? 0,
        monthData?.cancelled_count ?? 0,
        monthData?.inactive_count ?? 0,
    ];

    const categories = data.map((item) => moment(item.verification_date).format("DD MMM HH:mm"));

    const lineOptions = {
        chart: { type: "line", height: 350, toolbar: { show: true } },
        stroke: { curve: "smooth", width: 3 },
        xaxis: { categories, labels: { rotate: -45 } },
        yaxis: { min: 0 },
        colors: ["#2E7D32", "#D32F2F", "#ED6C02", "#6A1B9A", "#1976D2"],
        markers: { size: 5 },
        dataLabels: { enabled: false },
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false },
    };

    const lineSeries = [
        { name: "Active",    data: data.map((i) => i.active_count) },
        { name: "Suspended", data: data.map((i) => i.suspended_count) },
        { name: "Cancelled", data: data.map((i) => i.cancelled_count) },
        { name: "Inactive",  data: data.map((i) => i.inactive_count) },
    ];

    return (
        <Box p={3}>
            {/* ── BC Sales Stats Widget (top) ── */}
            <BcSalesStatsWidget />

            {/* ── GST Verification Header ── */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={600}>
                    BP GST VERIFICATION
                </Typography>
            </Box>

            {/* ── Line Chart ── */}
            <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        GST Status Trend
                    </Typography>
                    <ReactApexChart options={lineOptions} series={lineSeries} type="line" height={350} />
                </CardContent>
            </Card>

            {/* ── Month / Year filter + KPI cards ── */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Typography variant="h5" fontWeight={700}>
                    GST Verification Dashboard
                </Typography>

                <Box display="flex" gap={1.5} p={1} bgcolor="#fff" borderRadius={3} boxShadow={2}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Month</InputLabel>
                        <Select label="Month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                            {months.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 110 }}>
                        <InputLabel>Year</InputLabel>
                        <Select label="Year" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* ── GST KPI Cards ── */}
            <Grid container spacing={3}>
                {gstCards.map((card) => (
                    <Grid item xs={12} md={6} lg={2.4} key={card.title}>
                        <Card sx={{ borderRadius: 3, boxShadow: 3, borderTop: `4px solid ${card.color}` }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">{card.title}</Typography>
                                <Typography variant="h4" fontWeight={700} mt={1} color={card.color}>
                                    <CountUp end={card.value} duration={1.5} />
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* ── Pie Chart ── */}
            <Card sx={{ mt: 4, borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        GST BP Distribution
                    </Typography>
                    <ReactApexChart options={pieChartOptions} series={pieChartSeries} type="pie" height={400} />
                </CardContent>
            </Card>
        </Box>
    );
};

export default BpGstVerification;